import './App.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from './firebase.js';
import pic from './horn.webp';
import abdullahPhoto from './abdullah-security.webp';

const DEFAULT_ANGLE = '90';
const ACCESS_PIN = '2542';

function App() {
  const [value, setValue] = useState(DEFAULT_ANGLE);
  const [status, setStatus] = useState('OFF');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const pinInputRef = useRef(null);

  const refresh = useCallback(async () => {
    const snapshot = await getDoc(doc(db, 'Remote', 'Remote'));
    const data = snapshot.data();

    if (data) {
      setStatus(data.LED ?? 'OFF');
      setValue(data.angle === '-1' ? DEFAULT_ANGLE : String(data.angle ?? DEFAULT_ANGLE));
    }
  }, []);

  useEffect(() => {
    if (!isUnlocked) return;

    refresh().catch((error) => console.error('Could not load remote state:', error));
  }, [isUnlocked, refresh]);

  const unlockRemote = (event) => {
    event.preventDefault();

    if (pin === ACCESS_PIN) {
      setPinError('');
      setIsUnlocked(true);
      return;
    }

    setPin('');
    setPinError('Access denied. Abdullah detected. Try the code again.');
    setFailedAttempts((attempts) => attempts + 1);
    pinInputRef.current?.focus();
  };

  const setAngle = async (angle) => {
    await setDoc(
      doc(db, 'Remote', 'Remote'),
      { angle: String(angle) },
      { merge: true }
    );
  };

  const sendOff = async () => {
    // The Pico performs the action and writes 90 back to Firestore itself.
    // No browser timer is needed, so closing this page cannot strand -1.
    setValue(DEFAULT_ANGLE);
    await setAngle('-1');
  };

  const toggle = async () => {
    const nextStatus = status === 'ON' ? 'OFF' : 'ON';
    await updateDoc(doc(db, 'Remote', 'Remote'), { LED: nextStatus });
    setStatus(nextStatus);
  };

  const numericAngle = Number(value) || 0;

  return (
    <div className="main">
      <div
        className={`remote ${isUnlocked ? '' : 'remote--locked'}`}
        aria-hidden={!isUnlocked}
        inert={!isUnlocked ? true : undefined}
      >
        <div className="remotebuttons">
          <button onClick={toggle} className="on">LED</button>
          <button onClick={sendOff} className="off">OFF</button>
        </div>
        <div className="slider-cont">
          <input
            className="slider"
            type="range"
            min="0"
            max="115"
            step="1"
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
          <h1>angle : {value}</h1>
          <img
            src={pic}
            alt="Air conditioner direction"
            style={{ transform: `rotate(${360 - numericAngle}deg)` }}
          />
          <button onClick={() => setAngle(value)}>Set</button>
        </div>
        <h1>State : {status}</h1>
      </div>

      {!isUnlocked && (
        <div className="security-overlay">
          <section
            className={`security-gate ${pinError ? 'security-gate--error' : ''}`}
            key={failedAttempts}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="security-title"
            aria-describedby="security-message"
          >
            <h1 id="security-title">
              <span className="security-title__top">
                ANTI-ABDULLAH <span className="security-emoji" aria-hidden="true">🖕</span>
              </span>
              <span>SECURITY</span>
            </h1>

            <div className="security-photo" aria-hidden="true">
              <img src={abdullahPhoto} alt="" />
              <span className="security-cross security-cross--forward" />
              <span className="security-cross security-cross--back" />
            </div>

            <p id="security-message">Restricted remote. Enter the 4-digit clearance code.</p>

            <form onSubmit={unlockRemote} className="security-form">
              <label htmlFor="security-pin">Enter PIN</label>
              <div className="security-form__row">
                <input
                  ref={pinInputRef}
                  id="security-pin"
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="4"
                  value={pin}
                  onChange={(event) => {
                    setPin(event.target.value.replace(/\D/g, '').slice(0, 4));
                    setPinError('');
                  }}
                  aria-invalid={Boolean(pinError)}
                  aria-describedby={pinError ? 'pin-error' : undefined}
                  autoComplete="off"
                  autoFocus
                />
                <button type="submit" disabled={pin.length !== 4}>Unlock</button>
              </div>
              {pinError && (
                <p
                  id="pin-error"
                  className="security-form__feedback"
                  aria-live="polite"
                >
                  {pinError}
                </p>
              )}
            </form>
          </section>
        </div>
      )}
    </div>
  );
}

export default App;
